import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, CreditCard, QrCode, Lock, Wallet, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FormField, OptionCard } from "@/components/FormBits";
import { STORE_ADDRESS, STORE_HOURS } from "@/lib/store";
import { OrderItems } from "@/components/OrderItems";
import { ArtUpload } from "@/components/ArtUpload";
import { CashPayment, parseMoney } from "@/components/CashPayment";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, brl, formatError } from "@/lib/api";

const Section = ({ n, title, children }) => (
  <section className="space-y-4 rounded-md border border-slate-200 bg-white p-6">
    <h2 className="flex items-center gap-3 font-display text-lg font-bold">
      <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-900 text-xs text-white">{n}</span>{title}
    </h2>
    {children}
  </section>
);

const DELIVERY = { method: "retirada" };

export default function CheckoutPage() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("pix");
  const [methods, setMethods] = useState({ stripe: false, mercadopago: true });
  const [busy, setBusy] = useState(false);
  const [artFiles, setArtFiles] = useState([]);
  const [cash, setCash] = useState("");
  const orderRef = useRef(null);
  const isStore = user?.role === "store";
  const isCash = isStore && method === "cash";

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user && user.role !== "store") setCustomer((c) => ({ name: c.name || user.name || "", email: c.email || user.email || "", phone: c.phone || user.phone || "" }));
  }, [user]);

  if (!items.length && !busy) return <Navigate to="/carrinho" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!artFiles.length && !isStore) {
      document.querySelector("[data-testid='art-upload']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return toast.error("Envie o arquivo da arte antes de finalizar");
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, width: i.width, height: i.height })),
        customer: { ...customer, email: customer.email || null }, delivery: DELIVERY, notes, payment_method: isCash ? "cash" : method,
        art_file_ids: artFiles.map((f) => f.id), cash_received: isCash ? parseMoney(cash) : null,
      };
      if (!orderRef.current || isCash) orderRef.current = (await api.post("/orders", payload)).data;
      if (isCash) {
        const o = orderRef.current;
        clear();
        toast.success(o.payment_status === "paid" ? `Pedido #${o.number} finalizado e pago` : `Pedido #${o.number} finalizado · restante ${brl(o.amount_due)}`);
        return navigate("/conta");
      }
      if (method === "pix") {
        clear();
        return navigate(`/pix/${orderRef.current.id}`);
      }
      const { data } = await api.post("/payments/checkout", { order_id: orderRef.current.id, method, origin_url: window.location.origin });
      clear();
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy(false);
    }
  };

  const setC = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:px-8" data-testid="checkout-form">
      <div className="space-y-6 lg:col-span-7">
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Finalizar compra</h1>
        <Section n="1" title={isStore ? "Dados do cliente" : "Seus dados"}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="name" label="Nome completo" required value={customer.name} onChange={setC("name")} className="sm:col-span-2" />
            <FormField id="email" label={isCash ? "E-mail (opcional)" : "E-mail"} type="email" required={!isCash} value={customer.email} onChange={setC("email")} />
            <FormField id="phone" label="Telefone / WhatsApp" required minLength={8} value={customer.phone} onChange={setC("phone")} />
          </div>
        </Section>
        <Section n="2" title="Retirada na loja">
          <div className="flex items-start gap-3 rounded-md border-2 border-[#0099DD] bg-sky-50 p-4" data-testid="pickup-info">
            <Store className="mt-0.5 h-5 w-5 text-[#0099DD]" />
            <div className="text-sm">
              <p className="font-semibold text-slate-900">Retire seu pedido na Sanvalle Gráfica</p>
              <p className="text-slate-600">{STORE_ADDRESS}</p>
              <p className="text-slate-500">Horário: {STORE_HOURS}</p>
              <p className="mt-1 text-xs text-slate-500">Avisaremos pelo WhatsApp quando o pedido estiver disponível para retirada.</p>
            </div>
          </div>
        </Section>
        <Section n="3" title={isStore ? "Arquivo da arte (opcional)" : "Arquivo da arte"}>
          <p className="text-sm text-slate-500">Envie a arte pronta para impressão. Se tiver mais de um produto, envie um arquivo para cada.</p>
          <ArtUpload files={artFiles} onChange={setArtFiles} />
          <Textarea placeholder="Observações sobre a arte ou o pedido (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="input-notes" />
        </Section>
        <Section n="4" title="Pagamento">
          <div className="grid gap-3 sm:grid-cols-2">
            {methods.stripe && <OptionCard icon={CreditCard} title="Cartão de crédito" subtitle="Pagamento seguro via Stripe" checked={method === "stripe"} onClick={() => setMethod("stripe")} testId="payment-stripe" />}
            <OptionCard icon={QrCode} title="Pix" subtitle="QR Code e Pix copia e cola na hora" checked={method === "pix"} onClick={() => setMethod("pix")} testId="payment-pix" />
            <OptionCard icon={Wallet} title="Mercado Pago" subtitle="Cartão de crédito, débito ou saldo" checked={method === "mercadopago"} onClick={() => setMethod("mercadopago")} testId="payment-mercadopago" />
            {isStore && <OptionCard icon={Banknote} title="Dinheiro" subtitle="Recebido no balcão da loja" checked={method === "cash"} onClick={() => setMethod("cash")} testId="payment-cash" />}
          </div>
          {isCash && <CashPayment total={total} value={cash} onChange={setCash} />}
        </Section>
      </div>
      <aside className="lg:col-span-5">
        <div className="space-y-4 rounded-md border border-slate-200 bg-white p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-display text-lg font-bold">Resumo</h2>
          <OrderItems items={items} />
          <div className="flex items-end justify-between border-t border-slate-100 pt-4">
            <span className="text-sm text-slate-500">Total</span>
            <span className="font-mono text-3xl font-bold" data-testid="checkout-total">{brl(total)}</span>
          </div>
          {!artFiles.length && !isStore && <p className="text-xs text-amber-700" data-testid="art-required-hint">Envie o arquivo da arte para finalizar o pedido.</p>}
          <Button type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#E5007D] text-base font-bold hover:bg-[#c2006a]" data-testid="btn-place-order">
            {isCash ? <Banknote className="h-4 w-4" /> : <Lock className="h-4 w-4" />} {busy ? "Processando..." : isCash ? "Finalizar pedido" : "Confirmar e pagar"}
          </Button>
        </div>
      </aside>
    </form>
  );
}
